import { isInternalUser } from './utils';

// 5:00pm Australia/Sydney on Monday 7 September 2026 (AEST, UTC+10). Applications whose
// broker-submission instant is strictly before this keep the pre-KYB credit-approve and
// manual-KYC-SMS behaviour (ADO 1626).
export const KYB_GO_LIVE_CUTOFF_MS = Date.parse('2026-09-07T07:00:00.000Z');

export type KybGoLiveApplicationLike = {
  CreateTime?: string | Date | null;
  AppInfoSalesforceID?: string | null;
  InteflowStatus?: unknown;
};

const readCreditSubmittedAt = (inteflowStatus: unknown): string | Date | null => {
  let status = inteflowStatus;
  if (typeof status === 'string') {
    try {
      status = JSON.parse(status);
    } catch {
      return null;
    }
  }
  if (!status || typeof status !== 'object') {
    return null;
  }
  const value = (status as {ec1_Credit_Submitted_DateTime__c?: unknown}).ec1_Credit_Submitted_DateTime__c;
  if (value instanceof Date) {
    return value;
  }
  return typeof value === 'string' && value ? value : null;
};

const toEpochMs = (value: string | Date | null | undefined): number | null => {
  if (value == null || value === '') {
    return null;
  }
  const ms = value instanceof Date ? value.getTime() : Date.parse(String(value));
  return Number.isFinite(ms) ? ms : null;
};

/**
 * Broker submission instant. Prefers Salesforce `ec1_Credit_Submitted_DateTime__c` on the
 * stored Opportunity snapshot; falls back to `CreateTime` only when the application already
 * has a Salesforce id (it has left Draft). Unsubmitted applications return null.
 */
export const resolveApplicationSubmissionTimeMs = (
  application: KybGoLiveApplicationLike,
): number | null => {
  if (!application.AppInfoSalesforceID) {
    return null;
  }
  return toEpochMs(readCreditSubmittedAt(application.InteflowStatus) ?? application.CreateTime ?? null);
};

export const isApplicationSubmittedBeforeKybGoLive = (
  application: KybGoLiveApplicationLike,
): boolean => {
  const submittedMs = resolveApplicationSubmissionTimeMs(application);
  return submittedMs != null && submittedMs < KYB_GO_LIVE_CUTOFF_MS;
};

/**
 * Internal staff can skip privacy-consent issue/reissue on any application. Brokers can skip it
 * only on applications submitted before KYB go-live, where those fields were not collected at
 * origination. Digital ID re-verify stays internal-only.
 */
export const canSkipPrivacyConsentSideEffects = (
  user: Parameters<typeof isInternalUser>[0],
  application: KybGoLiveApplicationLike,
): boolean => isInternalUser(user) || isApplicationSubmittedBeforeKybGoLive(application);
